import {
    faCircleInfo,
    faEdit,
    faFileInvoiceDollar,
    faFileLines,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import ButtonAtom from "../../../components/atoms/button/button";
import ProjectHeader from "../../../components/ProjectHeader";
import useProjectPermission from "../../../hooks/useProjectPermission";
import { useUpdateProjectMutation } from "../../../store/services/projectsService";
import { fetchTemplates } from "../../../store/slices/templateSlice";
import {
    formatValueToLocale,
    getFormattedDate,
    showToast,
} from "../../../utils/method";
import AddProjectModal from "../add/add-project";
import "./project-overview.scss";
import ProjectSettingsRow from "./ProjectSettingsRow";
import { PROJECT_AREA, PROJECT_ACTION } from "../../../constant/projectPermissions";

// Safe percentage of a part against a whole, clamped to [0, 100].
const pct = (part, whole) => {
    const p = Number(part) || 0;
    const w = Number(whole) || 0;
    if (w <= 0) return 0;
    return Math.min(100, Math.max(0, (p / w) * 100));
};

const formatCurrency = (value) => `${formatValueToLocale(value ?? 0, true)}`;

// ── Small presentational helpers (props-only) ──
const KpiCard = ({ accent, label, value, sub }) => (
    <div className="po-kpi-card">
        <div className={`po-kpi-accent po-accent--${accent}`} />
        <div className="po-kpi-label">{label}</div>
        <div className="po-kpi-value">{value}</div>
        {sub ? <div className="po-kpi-sub">{sub}</div> : null}
    </div>
);

const InfoRow = ({ label, value, valueClass = "" }) => (
    <div className="po-info-row">
        <span className="po-info-label">{label}</span>
        <span className={`po-info-value ${valueClass}`}>{value}</span>
    </div>
);

const StatusRow = ({ tone, label, count, total }) => (
    <div className="po-status-row">
        <div className={`po-status-dot po-tone--${tone}`} />
        <span className="po-status-label">{label}</span>
        <div className="po-status-track">
            <div
                className={`po-status-fill po-tone--${tone}`}
                style={{ width: `${pct(count, total)}%` }}
            />
        </div>
        <span className="po-status-count">{count}</span>
    </div>
);

const StatusCard = ({ icon, title, total, children }) => {
    const { t } = useTranslation();
    return (
        <div className="po-card">
            <div className="po-card-header">
                <div className="po-card-title">
                    <FontAwesomeIcon icon={icon} />
                    {title}
                </div>
                <span className="po-card-header-total">
                    {t("projects.overview.total-count", { count: total })}
                </span>
            </div>
            <div className="po-card-body">
                <div className="po-status-breakdown">{children}</div>
            </div>
        </div>
    );
};

const ProjectOverview = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const { project: projectDetails } = useSelector(
        (state) => state.projectDetails,
    );
    const { templates } = useSelector((state) => state.templates);
    const [updateProject] = useUpdateProjectMutation();
    const { can, isLoading: permissionLoading } = useProjectPermission();
    const canEditProject = !permissionLoading && can(PROJECT_AREA.OVERVIEW, PROJECT_ACTION.EDIT);

    const projectName = projectDetails?.name || "";
    const [showEditModal, setShowEditModal] = useState(false);

    useEffect(() => {
        if (canEditProject) {
            dispatch(fetchTemplates());
        }
    }, [dispatch, canEditProject]);

    const handleEditProject = async (projectData) => {
        try {
            const { id, name, owner, address, projectKey, projectVolume, startDate } =
                projectData;
            await updateProject({
                id,
                name,
                owner,
                address,
                projectKey,
                projectVolume,
                startDate,
            }).unwrap();
            showToast(t("projects.update-success"), { type: "success" });
            setShowEditModal(false);
        } catch (error) {
            showToast(
                error?.data?.message || error?.message || t("errors.general"),
                { type: "error" },
            );
        }
    };

    if (!projectDetails) {
        return <ProjectHeader projectName={projectName} isOverview />;
    }

    // Basic-tier members receive the project shell without financial amounts
    // (the backend strips them). This payload flag drives whether the financial
    // sections render at all; the status counts below stay visible to everyone.
    const showFinancials = !!projectDetails.canViewFinancials;

    const budgetVolume = Number(projectDetails.projectVolume ?? 0);
    const lvVolume = Number(projectDetails.projectVolumeLv ?? 0);
    const nachtrageVolume = Number(projectDetails.projectVolumeNachtrage ?? 0);
    const totalVolume = lvVolume + nachtrageVolume;

    const lvCounts = projectDetails.lvCounts || {};
    const invoiceCounts = projectDetails.invoiceCounts || {};
    const lvTotal = lvCounts.total ?? (lvCounts.main || 0) + (lvCounts.nachtrag || 0);

    const lvPct = pct(lvVolume, budgetVolume);
    const nachPct = pct(nachtrageVolume, budgetVolume);
    const restPct = Math.max(0, 100 - lvPct - nachPct);

    const isLocal = projectDetails.isLocal ?? !projectDetails.capmoId;

    return (
        <>
            <ProjectHeader projectName={projectName} isOverview />

            <div className="po-overview">
                {/* ── KPI cards (financial tier only) ── */}
                {showFinancials && (
                <div className="po-kpi-row">
                    <KpiCard
                        accent="brand"
                        label={t("projects.overview.project-volume")}
                        value={formatCurrency(budgetVolume)}
                        sub={t("projects.overview.budget-target")}
                    />
                    <KpiCard
                        accent="success"
                        label={t("projects.overview.lv-volume")}
                        value={formatCurrency(lvVolume)}
                        sub={t("projects.overview.across-lvs", {
                            count: lvTotal,
                        })}
                    />
                    <KpiCard
                        accent="warn"
                        label={t("projects.overview.nachtrage-volume")}
                        value={formatCurrency(nachtrageVolume)}
                        sub={t("projects.overview.of-lv-volume", {
                            percent: Math.round(pct(nachtrageVolume, lvVolume)),
                        })}
                    />
                    <KpiCard
                        accent="info"
                        label={t("projects.overview.total-volume")}
                        value={formatCurrency(totalVolume)}
                        sub={t("projects.overview.of-budget", {
                            percent: Math.round(pct(totalVolume, budgetVolume)),
                        })}
                    />
                    <KpiCard
                        accent="ink"
                        label={t("projects.overview.invoices")}
                        value={invoiceCounts.total || 0}
                        sub={t("projects.overview.finalised-count", {
                            count: invoiceCounts.invoiceFinalized || 0,
                        })}
                    />
                </div>
                )}

                {/* ── Project info + status breakdown ── */}
                <div className="po-three-col">
                    {/* Project information */}
                    <div className="po-card">
                        <div className="po-card-header">
                            <div className="po-card-title">
                                <FontAwesomeIcon icon={faCircleInfo} />
                                {t("projects.project-info")}
                            </div>
                            <div className="po-card-header-actions">
                                {projectDetails.isArchived ? (
                                    <span className="po-badge po-badge--neutral">
                                        {t("filter-bar.status.archived")}
                                    </span>
                                ) : (
                                    <span className="po-badge po-badge--success">
                                        {t("projects.status.active")}
                                    </span>
                                )}
                                {isLocal && canEditProject && (
                                    <ButtonAtom
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => setShowEditModal(true)}
                                    >
                                        <FontAwesomeIcon
                                            icon={faEdit}
                                            className="me-2"
                                        />
                                        {t("common.edit")}
                                    </ButtonAtom>
                                )}
                            </div>
                        </div>
                        <div className="po-card-body">
                            <div className="po-info-table">
                                <InfoRow
                                    label={t("projects.name")}
                                    value={projectDetails.name}
                                />
                                <InfoRow
                                    label={t("projects.projectKey")}
                                    value={projectDetails.projectKey}
                                    valueClass="po-value--brand"
                                />
                                <InfoRow
                                    label={t("projects.address")}
                                    value={projectDetails.address}
                                />
                                <InfoRow
                                    label={t("projects.startDate")}
                                    value={getFormattedDate(
                                        projectDetails.startDate,
                                    )}
                                />
                                {showFinancials && (
                                    <>
                                <InfoRow
                                    label={t("projects.projectVolume")}
                                    value={formatCurrency(budgetVolume)}
                                />
                                <InfoRow
                                    label={t(
                                        "projects.settings.project-volume-lv",
                                    )}
                                    value={formatCurrency(lvVolume)}
                                />
                                <InfoRow
                                    label={t(
                                        "projects.settings.project-volume-nachtrage",
                                    )}
                                    value={formatCurrency(nachtrageVolume)}
                                />
                                <InfoRow
                                    label={t(
                                        "projects.settings.project-volume-total",
                                    )}
                                    value={formatCurrency(totalVolume)}
                                    valueClass="po-value--brand po-value--total"
                                />
                                    </>
                                )}
                            </div>

                            {/* Volume breakdown bar (financial tier only) */}
                            {showFinancials && budgetVolume > 0 && (
                                <div className="po-volume-bar-wrap">
                                    <div className="po-volume-bar">
                                        <div
                                            className="po-volume-bar-fill po-vb--lv"
                                            style={{ width: `${lvPct}%` }}
                                        />
                                        <div
                                            className="po-volume-bar-fill po-vb--nach"
                                            style={{ width: `${nachPct}%` }}
                                        />
                                    </div>
                                    <div className="po-vb-legend">
                                        <span className="po-vb-legend-item">
                                            <span className="po-vb-dot po-vb--lv" />
                                            {t("projects.overview.lv-volume")} (
                                            {Math.round(lvPct)}%)
                                        </span>
                                        <span className="po-vb-legend-item">
                                            <span className="po-vb-dot po-vb--nach" />
                                            {t(
                                                "projects.overview.nachtrage-volume",
                                            )}{" "}
                                            ({Math.round(nachPct)}%)
                                        </span>
                                        <span className="po-vb-legend-item">
                                            <span className="po-vb-dot po-vb--rest" />
                                            {t(
                                                "projects.overview.volume-remaining",
                                            )}{" "}
                                            ({Math.round(restPct)}%)
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Status cards */}
                    <div className="po-stack">
                        <StatusCard
                            icon={faFileLines}
                            title={t("projects.overview.lv-status")}
                            total={lvTotal}
                        >
                            <StatusRow
                                tone="brand"
                                label={t("projects.overview.lv-main")}
                                count={lvCounts.main || 0}
                                total={lvTotal}
                            />
                            <StatusRow
                                tone="info"
                                label={t("projects.overview.lv-nachtrag")}
                                count={lvCounts.nachtrag || 0}
                                total={lvTotal}
                            />
                        </StatusCard>

                        <StatusCard
                            icon={faFileInvoiceDollar}
                            title={t("projects.overview.invoice-status")}
                            total={invoiceCounts.total || 0}
                        >
                            <StatusRow
                                tone="success"
                                label={t("invoice.status.INVOICE_FINALIZED")}
                                count={invoiceCounts.invoiceFinalized || 0}
                                total={invoiceCounts.total || 0}
                            />
                            <StatusRow
                                tone="warn"
                                label={t("invoice.status.INVOICE_DRAFT")}
                                count={invoiceCounts.invoiceDraft || 0}
                                total={invoiceCounts.total || 0}
                            />
                            <StatusRow
                                tone="danger"
                                label={t("invoice.status.INVOICE_REJECTED")}
                                count={invoiceCounts.invoiceRejected || 0}
                                total={invoiceCounts.total || 0}
                            />
                        </StatusCard>
                    </div>
                </div>

                {/* ── Settings & configuration ── */}
                <ProjectSettingsRow
                    projectDetails={projectDetails}
                    templates={templates}
                />
            </div>

            {showEditModal && (
                <AddProjectModal
                    show={showEditModal}
                    onClose={() => setShowEditModal(false)}
                    onSubmit={handleEditProject}
                    mode="edit"
                    projectInfo={projectDetails}
                />
            )}
        </>
    );
};

export default ProjectOverview;
